import React from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Stack,
  IconButton,
  Divider,
  MenuItem,
  CircularProgress,
  Box,
  Paper,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

function CreatePackage({
  isEditMode,
  packageData,
  handlePackageChange,
  serviceDetails,
  addServiceField,
  removeServiceField,
  handleServiceChange,
  servicesList,
  subServicesMap,
  handleModalClose,
  handleSavePackage,
  submitLoading,
}) {
  const compactInputStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: "#fff",
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 250,
        borderRadius: "8px",
        mt: 0.5,
        "& .MuiMenuItem-root": {
          fontSize: "0.875rem",
        },
      },
    },
  };

  // === VALIDATION CHECKS ===
  const isPackageNameEmpty =
    !packageData.package_name || packageData.package_name.trim() === "";
  const isCostInvalid =
    !packageData.package_cost || Number(packageData.package_cost) <= 0;
  const isPackageDurationInvalid =
    !packageData.duration || Number(packageData.duration) <= 0;

  const hasZeroServiceDuration = serviceDetails.some(
    (service) => !service.duration || Number(service.duration) <= 0,
  );

  const hasDuplicateServices = serviceDetails.some(
    (service, index) =>
      service.service_id &&
      service.sub_service_id &&
      serviceDetails.findIndex(
        (s) =>
          s.service_id === service.service_id &&
          s.sub_service_id === service.sub_service_id,
      ) !== index,
  );

  const totalIncludedServiceDuration = serviceDetails.reduce(
    (total, service) => total + (Number(service.duration) || 0),
    0,
  );
  const hasInsufficientTotalServiceDuration =
    Boolean(packageData.duration) &&
    totalIncludedServiceDuration < Number(packageData.duration);
  const hasServiceDurationAbovePackageDuration = serviceDetails.some(
    (service) =>
      Boolean(packageData.duration) &&
      Number(service.duration) > Number(packageData.duration),
  );

  // The combined duration must meet the package duration, and no service may exceed it.
  const isTotalServiceDurationInvalid =
    hasInsufficientTotalServiceDuration ||
    hasServiceDurationAbovePackageDuration;

  const handleIncludedServiceDurationChange = (index, event) => {
    const enteredDuration = Number(event.target.value);
    if (
      packageData.duration &&
      enteredDuration > Number(packageData.duration)
    ) {
      return;
    }
    handleServiceChange(index, event);
  };

  // Overall form validity check to disable the Save Button if requirements are not met
  const isFormInvalid =
    isPackageNameEmpty ||
    isCostInvalid ||
    isPackageDurationInvalid ||
    hasZeroServiceDuration ||
    isTotalServiceDurationInvalid ||
    hasDuplicateServices;

  return (
    <form onSubmit={handleSavePackage}>
      <Box sx={{ p: 1 }}>
        {/* === PACKAGE DETAILS SECTION === */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <Inventory2OutlinedIcon sx={{ color: "#347B89" }} />
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              Package Details
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Package Name"
                name="package_name"
                value={packageData.package_name}
                onChange={handlePackageChange}
                required
                size="small"
                sx={compactInputStyle}
                error={
                  packageData.package_name !== "" &&
                  packageData.package_name.trim() === ""
                }
                helperText={
                  packageData.package_name !== "" &&
                  packageData.package_name.trim() === ""
                    ? "Package name cannot be empty spaces"
                    : ""
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Cost (₹)"
                name="package_cost"
                value={packageData.package_cost}
                onChange={handlePackageChange}
                inputProps={{ min: 1 }}
                onKeyDown={(e) =>
                  ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()
                }
                required
                size="small"
                sx={compactInputStyle}
                error={
                  Boolean(packageData.package_cost) &&
                  Number(packageData.package_cost) <= 0
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Duration (Days)"
                name="duration"
                value={packageData.duration}
                onChange={handlePackageChange}
                inputProps={{ min: 1 }}
                onKeyDown={(e) =>
                  ["e", "E", "+", "-", "."].includes(e.key) &&
                  e.preventDefault()
                }
                required
                size="small"
                sx={compactInputStyle}
                error={
                  Boolean(packageData.duration) &&
                  Number(packageData.duration) <= 0
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3, borderColor: "#e2e8f0" }} />

        {/* === SERVICE DETAILS SECTION === */}
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                Included Services
              </Typography>
            </Stack>

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addServiceField}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#347B89",
                bgcolor: "rgba(52, 123, 137, 0.1)",
                borderRadius: "6px",
                px: 2,
                "&:hover": { bgcolor: "rgba(52, 123, 137, 0.2)" },
              }}
            >
              Add Service
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            {serviceDetails.map((service, index) => {
              const isZeroDuration =
                Boolean(service.duration) && Number(service.duration) <= 0;
              const isServiceDurationTooHigh =
                Boolean(packageData.duration) &&
                Number(service.duration) > Number(packageData.duration);

              const isDuplicate =
                service.service_id &&
                service.sub_service_id &&
                serviceDetails.findIndex(
                  (s) =>
                    s.service_id === service.service_id &&
                    s.sub_service_id === service.sub_service_id,
                ) !== index;

              return (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    position: "relative",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f1f5f9" },
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    {/* ======================================= */}
                    {/* FIX APPLIED HERE: Main Service Dropdown */}
                    {/* ======================================= */}
                    <Grid item xs={12} sm={3.5}>
                      <TextField
                        select
                        fullWidth
                        label="Select Service"
                        name="service_id"
                        // Enforce String so MUI doesn't crash on matching values
                        value={
                          service.service_id ? String(service.service_id) : ""
                        }
                        onChange={(e) => handleServiceChange(index, e)}
                        required
                        size="small"
                        sx={compactInputStyle}
                        SelectProps={{ MenuProps: menuProps }}
                      >
                        {servicesList.map((srv) => (
                          <MenuItem key={srv.srv_id} value={String(srv.srv_id)}>
                            {srv.service_title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* ======================================= */}
                    {/* FIX APPLIED HERE: Sub Service Dropdown  */}
                    {/* ======================================= */}
                    <Grid item xs={12} sm={3.5}>
                      <TextField
                        select
                        fullWidth
                        label="Select Sub Service"
                        name="sub_service_id"
                        // Enforce String so MUI doesn't crash on matching values
                        value={
                          service.sub_service_id
                            ? String(service.sub_service_id)
                            : ""
                        }
                        onChange={(e) => handleServiceChange(index, e)}
                        required
                        size="small"
                        disabled={!service.service_id}
                        sx={compactInputStyle}
                        SelectProps={{ MenuProps: menuProps }}
                        error={isDuplicate}
                        helperText={isDuplicate ? "Duplicate Service" : ""}
                      >
                        {/* 
                          Added a fallback to check both Number and String representations
                          of the service_id. This prevents empty dropdowns in Edit mode 
                          when the API returns IDs as strings.
                        */}
                        {(
                          subServicesMap[service.service_id] ||
                          subServicesMap[Number(service.service_id)] ||
                          subServicesMap[String(service.service_id)] ||
                          []
                        ).map((sub) => (
                          <MenuItem
                            key={sub.sub_srv_id}
                            value={String(sub.sub_srv_id)}
                          >
                            {sub.recommomded_service || sub.sub_service_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={6} sm={2.5}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Duration (Days)"
                        name="duration"
                        value={service.duration}
                        onChange={(e) =>
                          handleIncludedServiceDurationChange(index, e)
                        }
                        inputProps={{
                          min: 1,
                          ...(packageData.duration
                            ? { max: Number(packageData.duration) }
                            : {}),
                        }}
                        onKeyDown={(e) =>
                          ["e", "E", "+", "-", "."].includes(e.key) &&
                          e.preventDefault()
                        }
                        required
                        size="small"
                        sx={compactInputStyle}
                        error={isZeroDuration || isServiceDurationTooHigh}
                        helperText={
                          isZeroDuration
                            ? "Must be > 0"
                            : isServiceDurationTooHigh
                              ? `Cannot exceed ${packageData.duration} days`
                              : ""
                        }
                      />
                    </Grid>

                    <Grid item xs={2} sm={1} sx={{ textAlign: "right", pt: 1 }}>
                      {serviceDetails.length > 1 && (
                        <Tooltip title="Remove Service" placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeServiceField(index)}
                            sx={{
                              bgcolor: "rgba(239, 68, 68, 0.1)",
                              "&:hover": { bgcolor: "rgba(239, 68, 68, 0.2)" },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        </Box>

        {/* === ACTIONS SECTION === */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ mt: 4 }}
        >
          {hasInsufficientTotalServiceDuration && (
            <Typography
              variant="body2"
              color="error"
              sx={{ alignSelf: "center", mr: 2, fontWeight: 600 }}
            >
              * Total included service duration ({totalIncludedServiceDuration}{" "}
              days) must be at least package duration ({packageData.duration} days).
            </Typography>
          )}

          <Button
            onClick={handleModalClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              color: "#64748b",
              borderColor: "#cbd5e1",
              borderRadius: "6px",
              fontWeight: 600,
              "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitLoading || isFormInvalid}
            sx={{
              textTransform: "none",
              backgroundColor: "#347B89",
              borderRadius: "6px",
              fontWeight: 600,
              minWidth: "140px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#265d68", boxShadow: "none" },
              "&.Mui-disabled": {
                backgroundColor: "#cbd5e1",
                color: "#64748b",
              },
            }}
          >
            {submitLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isEditMode ? (
              "Update Package"
            ) : (
              "Save Package"
            )}
          </Button>
        </Stack>
      </Box>
    </form>
  );
}

export default CreatePackage;
